import { Bell, MessageSquarePlus } from 'lucide-react'
import type { SalonNote } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatRelative } from '@/shared/lib/format.ts'
import { Button } from '@/shared/ui/button.tsx'
import { kyivToday, nextReminder, reminderWhen } from '../reminders.ts'
import { Sub } from './salon-cells.tsx'

interface NotesCellProps {
  notes: readonly SalonNote[]
  onOpen: () => void
  /** On a phone card the way into the thread is a full-size button under the latest note. */
  layout?: 'table' | 'card'
}

function LatestNote({ notes, clamp }: { notes: readonly SalonNote[]; clamp: string }) {
  const [latest] = notes
  if (!latest) return null

  const more = notes.length - 1
  return (
    <>
      <span className={cn('whitespace-pre-line text-ink [overflow-wrap:anywhere]', clamp)}>{latest.text}</span>
      <Sub>
        {latest.authorName} · {formatRelative(latest.createdAt)}
        {more > 0 && ` · ещё ${more}`}
      </Sub>
    </>
  )
}

/** The nearest open reminder on the row: someone promised to call back, and when. */
function NextReminder({ notes }: { notes: readonly SalonNote[] }) {
  const reminder = nextReminder(notes)
  if (!reminder) return null

  const today = kyivToday()
  return (
    <span
      className={cn(
        'mt-1 inline-flex items-center gap-1 text-[12px]',
        reminder.remindOn <= today ? 'font-medium text-warn-ink' : 'text-ink-muted',
      )}
    >
      <Bell className="size-3 shrink-0" aria-hidden />
      {reminderWhen(reminder.remindOn, today)} · {reminder.authorName}
    </span>
  )
}

/** The latest team comment on the row and the way into the whole thread. */
export function NotesCell({ notes, onOpen, layout = 'table' }: NotesCellProps) {
  if (layout === 'card') {
    return (
      <div className="flex flex-col items-start gap-1.5">
        <LatestNote notes={notes} clamp="line-clamp-3" />
        <NextReminder notes={notes} />
        <Button variant="secondary" size="sm" className="mt-1 min-h-11 w-full" onClick={onOpen}>
          {notes.length > 0 ? `Все комментарии · ${notes.length}` : 'Написать комментарий'}
        </Button>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      title={notes.length > 0 ? 'Открыть комментарии' : 'Написать комментарий'}
      className="group -mx-1.5 -my-1 flex w-[calc(100%+12px)] flex-col items-start rounded-control px-1.5 py-1 text-left hover:bg-surface-hover"
    >
      {notes.length > 0 ? (
        <>
          <LatestNote notes={notes} clamp="line-clamp-2" />
          <NextReminder notes={notes} />
        </>
      ) : (
        <span className="inline-flex min-h-8 items-center gap-1.5 text-[13px] text-ink-muted group-hover:text-ink">
          <MessageSquarePlus className="size-3.5" aria-hidden />
          Написать
        </span>
      )}
    </button>
  )
}
